import React, {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
} from "react";
import "../DetailView.css";
import { Claim, TriageDetails } from "../../../../types";
import Button from "react-bootstrap/Button";
import { ButtonWrapper } from "../../styled";
import { useDispatch, useSelector } from "react-redux";
import { openTriage } from "../../../../store/data/thunks";
import ErrorAlert from "../../../components/error-alert/ErrorAlert";
import RetryButton from "../../../components/error-alert/RetryButton";
import { getStatusBadgeColor } from "../../../utils";
import Alert from "react-bootstrap/Alert";
import { Client, Frame } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BASE_URL } from "../../../appConstants";
import { State } from "../../../../store/state";

type TriageProps = {
  dataSelected: Claim;
  triageDetails: TriageDetails;
  requestGetTriageDetailsByClaimId: (claimId: number) => Promise<any>;
};

const Triage: FunctionComponent<TriageProps> = (props) => {
  const { dataSelected, triageDetails, requestGetTriageDetailsByClaimId } =
    props;
  const dispatch = useDispatch();
  const [showError, setShowError] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const accessToken = useSelector((state: State) => state.authentication.accessToken);

  const loadTriage = useCallback(() => {
    setIsFetching(true);
    setShowError(false);
    requestGetTriageDetailsByClaimId(dataSelected.id)
      .catch(() => setShowError(true))
      .finally(() => setIsFetching(false));
  }, [requestGetTriageDetailsByClaimId, dataSelected.id]);

  useEffect(() => {
    loadTriage();
  }, [loadTriage]);

  useEffect(() => {
    
    const WS_ENDPOINT = `${BASE_URL}/mps/ws`; 
    console.log('[Triage] Connecting STOMP client to', WS_ENDPOINT);

    const client = new Client({
      // Use SockJS as WebSocket transport
      webSocketFactory: () => new SockJS(`${WS_ENDPOINT}`),
      
      // Add authorization header with token
      connectHeaders: {
        'Authorization': `Bearer ${accessToken}`,
      },
  
      // Debug callback: logs every STOMP frame & event
      debug: (msg: string) => {
        console.debug('[STOMP DEBUG]', msg);
      },
    });
  
    client.onStompError = (frame: Frame) => {
      console.error('[STOMP ERROR]', frame);
    };
  
    client.onWebSocketError = (evt: Event) => {
      console.error('[WS ERROR]', evt);
    };
  
    client.onWebSocketClose = (evt: CloseEvent) => {
      console.warn('[WS CLOSED]', evt);
    };
  
    client.onConnect = (frame: Frame) => {
      console.log('[STOMP CONNECTED]', frame);
      console.log('[STOMP SUBSCRIBE] /topic/triage/', dataSelected.id);
      client.subscribe(`/topic/triage/${dataSelected.id}`, () => {
        console.log('[STOMP MESSAGE] ping for claim', dataSelected.id);
        loadTriage();
      });
    };
  
    client.activate();
  
    return () => {
      console.log('[Triage] Deactivating STOMP client');
      client.deactivate();
    };
  }, [dataSelected.id, loadTriage, accessToken]);


  const getProgressTitle = (progress: String) => {
    if (!progress) {
      return <div>Awaiting Triage</div>;
    }

    switch (progress) {
      case "TRIAGE_STARTED":
        return "Started"
      case "TRIAGE_CANNOT_PROCEED":
        return "Authorisation not confirmed!";
      case "OWNER_CONFIRMATION_COMPLETED":
        return "In Progress";
      case "GENERAL_COMPLETED":
        return "In Progress";
      case "DAMAGE_COMPLETED":
        return "In Progress";
      case "FULLY_COMPLETED":
        return "Completed";
      default:
        return "Unknown";
    }
  };

  const getProgressDescription = (progress: String, clickedDate: String) => {
    if (!progress) {
      if (!clickedDate) {
        return <div>The user has not yet accessed the triage.</div>;
      } else {
        return (
          <div>
            The user has opened triage link, but no further action taken.
          </div>
        );
      }
    }
      
    switch (progress) {
      case "TRIAGE_STARTED":
        return "The triage has just been started. No data has been submitted yet."
      case "TRIAGE_CANNOT_PROCEED":
        return "Policy-holder or authorised-representative status has not been confirmed.";
      case "OWNER_CONFIRMATION_COMPLETED":
        return "The triage process has been initiated and the status as the current policy holder or authorized individual has been verified. Beyond this confirmation, no additional details have been submitted yet.";
      case "GENERAL_COMPLETED":
        return "The user is currently progressing through the triage form. While general information has been provided, sections specific to the type of loss, remain incomplete.";
      case "DAMAGE_COMPLETED":
        return "The user is nearly finished with the triage; only some general notes remain incomplete.";
      case "CONTENTS_COMPLETED":
        return "The user has submitted details about their contents items, completing the triage process.";
      case "FULLY_COMPLETED":
        return "The user has successfully completed the triage.";
      default:
        return "Unknown";
    }
  };

  const getProgress = (progress: string) => {
    return (
      <span
        className="badge badge-pill"
        style={{
          padding: "0.25rem 0.5rem",
          fontWeight: 500,
          fontSize: "11px",
          color: getStatusBadgeColor(progress).text,
          backgroundColor: getStatusBadgeColor(progress).background,
        }}
      >
        {getProgressTitle(progress)}
      </span>
    );
  };

  const isInvalidLossType = () => {
    /*  1 - Landslip
        2 - Earthquake
        3 - Flood
        4 - Fire
        5 - Storm
        6 - Impact
        9 - Hail
        10 - Malicious Damage
        11 - Meth Contamination
        12 - Water Damage
        14 - Land
        18 - Other
        23 - Rain
        33 - Landslide
      */
      // return [1, 2, 14, 33].includes(
      //   triageDetails.lossTypeId
      // );
      return false;
    };
  
  const canStartTriage = () => {
    return !(
      triageDetails.progress === "FULLY_COMPLETED" 
      //|| triageDetails.progress === "TRIAGE_CANNOT_PROCEED" 
    );
  };

  const isClaimActive = () =>
    ["New", "In Progress", "Re-Opened", "On Hold", "Hold Insurer"].includes(dataSelected.status);

  const getTriageButtonTitle = () => {
    return !triageDetails.progress ? "Begin New Triage" : "Proceed Triage";
  };

  const getSubmittedBy = () => {
    if (!triageDetails.submittedBy) {
      return "-";
    }

    const isInternal =
      triageDetails.submittedBy.includes("@morganprojects.") ||
      triageDetails.submittedBy.includes("@suncorp.") ||
      triageDetails.submittedBy.includes("@vero.");

    return (
      <div>
        {triageDetails.submittedBy}{" "}
        <span
          className="badge badge-pill"
          style={{
            padding: "0.25rem 0.5rem",
            fontWeight: 500,
            fontSize: "11px",
            color: isInternal ? "rgb(189,73,29)" : "rgb(64,46,1)",
            backgroundColor: isInternal
              ? "rgba(241,93,38, 0.2)"
              : "rgba(138,100,3, 0.2)",
          }}
        >
          {isInternal ? "Internal" : "Insured"}
        </span>
      </div>
    );
  };

  const getTriage = () => {
    if (!triageDetails) {
      return <div>Currently no triage data.</div>;
    }

    if (!dataSelected.lossType && dataSelected.lossType.trim().length === 0) {
      return (
        <Alert variant={"warning"}>
          No loss type specified for this claim. Please specify a loss type
          before proceeding with triage.
        </Alert>
      );
    }

    return (
      <div>
        <div className="format-section">
          <div className="format-fields">
            <div className="label">Progress</div>
            <div className="value">{getProgress(triageDetails.progress)}</div>
            <div style={{ paddingTop: "0.5rem" }} className="value">
              {getProgressDescription(
                triageDetails.progress,
                triageDetails.clickedDate
              )}
            </div>
          </div>
        </div>

        <div className="format-section">
          <div className="format-fields">
            <div className="label">Invitation Link Sent</div>
            <div className="value">
              {triageDetails.sentDate
                ? new Date(triageDetails.sentDate).toLocaleDateString("en-NZ")
                : "not sent"}
            </div>
          </div>

          <div className="format-fields">
            <div className="label">Invitation Link Opened</div>
            <div className="value">
              {triageDetails.clickedDate
                ? new Date(triageDetails.clickedDate).toLocaleDateString(
                    "en-NZ"
                  )
                : "not opened"}
            </div>
          </div>
        </div>

        <div className="format-section">
          <div className="format-fields">
            <div className="label">Triage Form Submitted</div>
            <div className="value">
              {triageDetails.submittedDate
                ? new Date(triageDetails.submittedDate).toLocaleDateString(
                    "en-NZ"
                  )
                : "not submitted"}
            </div>
          </div>

          <div className="format-fields">
            <div className="label">By</div>
            <div className="value">{getSubmittedBy()}</div>
          </div>
        </div>

        <div>
          {(canStartTriage() && isClaimActive() && !isInvalidLossType()) ? (
            <ButtonWrapper style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {triageDetails.progress && (
                <Alert className="d-inline-flex align-items-center mb-0"
                  variant="danger"
                  style={{ marginRight: "2rem", marginBottom: 0, display: "inline-block" }}
                >
                   <i className="fa fa-exclamation-triangle fa-lg text-danger mr-4" aria-hidden="true"></i>
                  A triage session has already been started for this claim. Before continuing, check that no other team member or insured is still working on it.
                </Alert>
              )}

              <Button
                variant="primary"
                style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
                onClick={() =>
                  dispatch(
                    openTriage(dataSelected.id)
                  )
                }
              >
                {getTriageButtonTitle()}
              </Button>
            </ButtonWrapper>
          ) : !isClaimActive() ? (
            <Alert variant={"warning"}>
          
              <div>
                This claim cannot be triaged because the claim status is &nbsp;
                <span
                  className="badge badge-pill"
                  style={{
                    padding: "0.25rem 0.5rem",
                    fontWeight: 500,
                    marginRight: "0.5rem",
                    marginBottom: "0.25rem",
                    fontSize: "11px",
                    color: getStatusBadgeColor(dataSelected.status).text,
                    backgroundColor: getStatusBadgeColor(dataSelected.status)
                      .background
                  }}
                >
                  {dataSelected.status}
                </span>

            </div></Alert>
           ) : (isInvalidLossType() ? (
            <Alert variant={"warning"}>
            <div>This claim cannot be triaged because the loss type {dataSelected.lossType} is currently not supported.</div>
            </Alert>
           ): null) }
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="detail-wrapper">
        <div className="heading">New Triage Web Form</div>
        {isFetching && (
          <div
            className="d-flex justify-content-center"
            style={{
              paddingTop: "1rem",
            }}
          >
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
        {showError && !isFetching && (
          <div
            style={{
              display: "flex",
              flex: "0 0 auto",
              paddingTop: "1rem",
              justifyContent: "center",
            }}
          >
            <ErrorAlert lightMode>
              <span style={{ paddingRight: "1rem" }}>
                Error while loading the triage details of the selected claim.
                Please retry or submit a Support Desk ticket.
              </span>
              <RetryButton retryFunction={loadTriage} />
            </ErrorAlert>
          </div>
        )}
        {!showError && !isFetching && getTriage()}
      </div>
    </div>
  );
};

export default Triage;
